import React, { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import * as S from '@/components/Ad/Status/Status.styles';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { AdStatus } from '@/types';
import { Button } from '@/components/UI/Button/Button';
import { useAuthStore } from '@/store/useAuthStore';
import { useAdDetails } from '@/hooks/useAdDetails';
import { GetUserResponse } from '@/api/types/user.types';
import { GET_USER } from '@/api/queries/user.queries';
import { PROLONG_ADS } from '@/api/mutations/ads.mutations';
import {
  PayForAdByPrepaidBundleResponse,
  ProlongAdsResponse,
} from '@/api/types/ads.types';
import { PAY_FOR_AD_BY_PREPAID_BUNDLE } from '@/api/mutations/payments.mutations';
import { useCurrentCategoryDetails } from '@/hooks/useCurrentCategory/useCurrentCategoryDetails';
import { useServerDataContext } from '@/contexts';
import { QUERY } from '@/constants/common';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';

interface StatusCardProps {
  status: AdStatus;
  isAuthor: boolean;
  onSuccess?: () => void;
  paymentFlowToggle: () => void;
  payByBalancePopupToggle: () => void;
  prolongByBalancePopupToggle: () => void;
}

export const StatusCard: React.FC<StatusCardProps> = ({
  status,
  isAuthor,
  onSuccess,
  paymentFlowToggle,
  payByBalancePopupToggle,
  prolongByBalancePopupToggle,
}) => {
  const { t } = useTranslation();
  const { replace } = useRouter();
  const { url, hash } = useUrlAndHashFromPath();
  const isProlongHashHandledRef = useRef(false);

  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const { categoryDetails, isCategoryDetailsLoading } =
    useCurrentCategoryDetails();

  const { adDetails, refetch: refetchAdDetails } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const {
    id,
    rejectReasons,
    rejectReason,
    isPayFromBundleAvailable,
    isProlongFree,
    hasEditPendingApproval,
  } = adData ?? {};

  const { data: userInfo } = useQuery<GetUserResponse>(GET_USER);

  const [prolongAds, { loading: prolongAdLoading }] =
    useMutation<ProlongAdsResponse>(PROLONG_ADS, {
      onCompleted: data => {
        refetchAdDetails().then(() => {
          if (data.prolongAds.entity) {
            onSuccess?.();
          }
        });
      },
    });
  const [payForAdByPrepaidBundle, { loading: payForAdByPrepaidBundleLoading }] =
    useMutation<PayForAdByPrepaidBundleResponse>(PAY_FOR_AD_BY_PREPAID_BUNDLE, {
      onCompleted: () => {
        refetchAdDetails();
      },
    });

  const isB2B = isAuthenticated && userInfo?.currentUser?.isActiveShop;
  const isShopBalanceSufficient =
    !!userInfo?.currentUser && userInfo.currentUser.shopPaidAdsBalance >= 1;
  const isFreeLimitExceeded = !!categoryDetails?.isFreeLimitExceededForUser;
  const isRestoreAvailable =
    isAuthor && [AdStatus.Expired, AdStatus.Archived].includes(status);

  function onPayButtonClick() {
    if (!adData || !userInfo?.currentUser) return;

    if (isPayFromBundleAvailable) {
      if (isB2B) {
        payForAdByPrepaidBundle({ variables: { adId: id } });
        return;
      }

      payByBalancePopupToggle();
      return;
    }

    paymentFlowToggle();
  }

  function onRestoreButtonClick() {
    if (!adData || !userInfo?.currentUser) return;

    if (isB2B) {
      if (
        isPayFromBundleAvailable ||
        isShopBalanceSufficient ||
        isProlongFree
      ) {
        prolongAds({ variables: { adIds: [id] } });
        return;
      }
      paymentFlowToggle();
      return;
    }

    if (isProlongFree || !isFreeLimitExceeded) {
      prolongAds({ variables: { adIds: [id] } });
      return;
    }

    if (isPayFromBundleAvailable) {
      prolongByBalancePopupToggle();
      return;
    }

    paymentFlowToggle();
  }

  useEffect(() => {
    if (hash !== QUERY.prolong) {
      isProlongHashHandledRef.current = false;
      return;
    }
    if (isProlongHashHandledRef.current) return;
    if (!isRestoreAvailable) return;
    if (!adData || !userInfo?.currentUser) return;
    if (isCategoryDetailsLoading) return;

    isProlongHashHandledRef.current = true;

    replace(url, undefined, { scroll: false }).then(() => {
      onRestoreButtonClick();
    });
  }, [
    hash,
    adData,
    userInfo?.currentUser,
    isCategoryDetailsLoading,
    isRestoreAvailable,
  ]);

  if (status === AdStatus.Published && !hasEditPendingApproval) {
    return null;
  }

  const texts = {
    [AdStatus.Pending]: t('pages.ad.ad_pending'),
    [AdStatus.Expired]: t('pages.ad.ad_inactive'),
    [AdStatus.AwaitingPayment]: (
      <Trans
        i18nKey="pages.ad.ad_limit"
        values={{ categoryName: categoryDetails?.name }}
      />
    ),
    [AdStatus.Rejected]: (
      <>
        {t('pages.ad.ad_rejected')}
        {rejectReasons?.length ? (
          <ul>
            {rejectReasons?.map(el => {
              return <li key={el.id}>{el.template}</li>;
            })}
          </ul>
        ) : (
          <S.SingleRejectReason>{rejectReason}</S.SingleRejectReason>
        )}
      </>
    ),
    [AdStatus.Archived]: t('pages.ad.ad_archived'),
    [AdStatus.Published]: t('pages.ad.changes_will_be_applied_after_review'),
  };

  return (
    <S.BoxWrapper>
      <S.Box $status={status}>
        <S.BoxStrip $status={status} />
        <S.BoxCard
          $status={status}
          data-testid={AdPageTestIds.statusBanner.banner}
        >
          <S.StatusText data-testid={AdPageTestIds.statusBanner.text}>
            {texts[status]}
          </S.StatusText>
        </S.BoxCard>
      </S.Box>

      {status === AdStatus.AwaitingPayment && (
        <S.ButtonBox>
          <Button
            data-testid={AdPageTestIds.statusBanner.button}
            isFullWidth
            height={54}
            borderRadius={14}
            appearance="secondary"
            onClick={onPayButtonClick}
            disabled={payForAdByPrepaidBundleLoading}
          >
            {t('common.pay')}
          </Button>
        </S.ButtonBox>
      )}

      {isRestoreAvailable && (
        <S.ButtonBox>
          <Button
            data-testid={AdPageTestIds.statusBanner.button}
            appearance="secondary"
            isFullWidth
            borderRadius={14}
            height={54}
            onClick={onRestoreButtonClick}
            disabled={prolongAdLoading}
          >
            {t('pages.ad.restore')}
          </Button>
        </S.ButtonBox>
      )}
    </S.BoxWrapper>
  );
};
